import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Users, Heart, Building, Handshake, 
  CheckCircle, ArrowRight, Share2, MapPin, Megaphone,
  Award, Star, Crown, Shield, DollarSign, Globe
} from "lucide-react";

const involvementTypes = [
  {
    id: "volunteer",
    icon: Users,
    title: "Volunteer",
    description: "Join our team of dedicated volunteers and help spread awareness in your community."
  },
  {
    id: "sponsor",
    icon: Building,
    title: "Become a Sponsor",
    description: "Support our mission at a level that fits your organization and gain meaningful visibility."
  },
  {
    id: "partner",
    icon: Handshake,
    title: "Partner With Us",
    description: "Collaborate with Blue vs Blue on initiatives that create lasting community impact."
  },
  {
    id: "supporter",
    icon: Heart,
    title: "Join our Community",
    description: "Stay connected and receive updates about our programs, events, and impact."
  }
];

const sponsorshipLevels = [
  {
    name: "Blue Ribbon Sponsor",
    icon: Award,
    amount: "$500 - $999",
    color: "bg-sky-50 border-sky-200",
    iconColor: "text-sky-500",
    benefits: [
      "Logo on event materials",
      "Social media recognition",
      "2 complimentary event tickets",
      "Certificate of appreciation"
    ]
  },
  {
    name: "Shield Sponsor",
    icon: Shield,
    amount: "$1,000 - $4,999",
    color: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
    benefits: [
      "Everything in Blue Ribbon, plus:",
      "Logo on website partner bar",
      "4 complimentary event tickets",
      "Booth space at one event",
      "Quarterly impact report"
    ]
  },
  {
    name: "Guardian Sponsor",
    icon: Star,
    amount: "$5,000 - $9,999",
    color: "bg-indigo-50 border-indigo-200",
    iconColor: "text-indigo-600",
    benefits: [
      "Everything in Shield, plus:",
      "Featured sponsor at one event",
      "8 complimentary event tickets",
      "Branded mobile unit recognition",
      "Speaking opportunity at gala"
    ]
  },
  {
    name: "Champion Sponsor",
    icon: Crown,
    amount: "$10,000+",
    color: "bg-amber-50 border-amber-200",
    iconColor: "text-amber-600",
    benefits: [
      "Everything in Guardian, plus:",
      "Title sponsor for one event",
      "VIP table at annual gala (10 seats)",
      "Co-branded community outreach",
      "Year-round logo on mobile unit",
      "Exclusive donor recognition plaque"
    ]
  }
];

const opportunities = [
  {
    title: "Social Media Ambassador",
    icon: Share2,
    description: "Help amplify our message across social media platforms. As an ambassador, you'll share our content, create awareness posts, and help us reach new audiences. We provide content guides, branded graphics, and monthly themes to make it easy.",
    commitment: "2-3 hours/week",
    skills: "Social media savvy, passion for the cause"
  },
  {
    title: "Community Outreach Coordinator",
    icon: MapPin,
    description: "Lead outreach efforts in your local community. Coordinators organize awareness events at barbershops, churches, fire stations, and community centers. You'll be the face of Blue vs Blue in your area, connecting with local leaders and spreading the word about early detection.",
    commitment: "5-10 hours/month",
    skills: "Strong communication, community connections"
  },
  {
    title: "Corporate Sponsor",
    icon: Building,
    description: "Align your organization with a meaningful cause. Corporate sponsors receive brand visibility across our events, website, mobile screening unit, and marketing materials. Choose a sponsorship level that fits your budget and goals. See sponsorship levels above for full details.",
    commitment: "Annual commitment",
    skills: "Any business looking to make an impact"
  }
];

export default function GetInvolvedPage() {
  const [activeTab, setActiveTab] = useState("volunteer");
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", organization: "",
    message: "", interests: [], receiveUpdates: true
  });
  const [submitting, setSubmitting] = useState(false);

  const interestOptions = [
    "Community Events", "Fundraising", "Health Screenings",
    "Educational Programs", "Social Media", "Administrative Support"
  ];

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/supporters`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        type: activeTab, interests: formData.interests,
        notes: `${formData.organization ? `Organization: ${formData.organization}\n` : ''}${formData.message}${formData.receiveUpdates ? '\n[Opted in for email updates]' : ''}`
      });
      toast.success("Thank you for your interest! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", organization: "", message: "", interests: [], receiveUpdates: true });
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="gradient-hero py-16" data-testid="involved-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Get Involved</span>
            <h1 className="text-heading-1 text-white mt-4 mb-4">Join Our Mission</h1>
            <p className="text-white/80 text-lg">
              There are many ways to support Blue vs Blue and help us spread prostate cancer 
              awareness. Find the right opportunity for you.
            </p>
          </div>
        </div>
      </section>

      {/* Options Grid */}
      <section className="py-12 bg-slate-50" data-testid="involvement-options">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {involvementTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setActiveTab(type.id)}
                className={`text-left p-6 rounded-lg transition-all ${
                  activeTab === type.id 
                    ? 'bg-deep-blue text-white shadow-lg' 
                    : 'bg-white border border-slate-200 hover:shadow-md'
                }`}
                data-testid={`option-${type.id}`}
              >
                <type.icon className={`w-8 h-8 mb-4 ${activeTab === type.id ? 'text-awareness-blue' : 'text-awareness-blue'}`} />
                <h3 className={`font-bold text-lg mb-2 ${activeTab === type.id ? 'text-white' : 'text-deep-blue'}`}>
                  {type.title}
                </h3>
                <p className={`text-sm ${activeTab === type.id ? 'text-white/80' : 'text-slate-600'}`}>
                  {type.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship Levels - shown when sponsor tab is active */}
      {activeTab === "sponsor" && (
        <section className="section-spacing bg-white" data-testid="sponsorship-levels">
          <div className="container-custom">
            <div className="text-center mb-10">
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Sponsorship Tiers</span>
              <h2 className="text-heading-2 text-deep-blue mt-2">Choose Your Level of Impact</h2>
              <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
                Every sponsorship level includes meaningful recognition and a direct contribution to prostate cancer awareness and early detection programs.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {sponsorshipLevels.map((level) => (
                <div key={level.name} className={`rounded-xl border-2 ${level.color} p-6 flex flex-col`} data-testid={`sponsor-level-${level.name.toLowerCase().replace(/\s/g, '-')}`}>
                  <level.icon className={`w-10 h-10 ${level.iconColor} mb-3`} />
                  <h3 className="font-bold text-deep-blue text-lg">{level.name}</h3>
                  <div className="text-2xl font-bold text-deep-blue mt-1 mb-4">{level.amount}</div>
                  <ul className="space-y-2 flex-1">
                    {level.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-awareness-blue shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col gap-2 mt-6">
                    <Button
                      className="btn-primary w-full"
                      onClick={async () => {
                        try {
                          const priceNum = parseInt(level.amount.replace(/[^0-9]/g, ''));
                          await axios.post(`${API}/cart/add`, {
                            item_type: "sponsorship",
                            sponsorship_level: level.name,
                            price: priceNum,
                            quantity: 1,
                            description: `${level.name} Sponsorship`
                          }, { withCredentials: true });
                          toast.success(`${level.name} added to cart!`);
                        } catch (err) {
                          if (err.response?.status === 401) {
                            setFormData(prev => ({...prev, message: `Interested in ${level.name} (${level.amount})`}));
                            document.getElementById('involvement-form')?.scrollIntoView({ behavior: 'smooth' });
                          } else toast.error("Failed to add to cart");
                        }
                      }}
                      data-testid={`select-level-${level.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      Add to Cart
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => {
                      setFormData(prev => ({...prev, message: `Interested in ${level.name} (${level.amount})`}));
                      document.getElementById('involvement-form')?.scrollIntoView({ behavior: 'smooth' });
                    }}>
                      Inquire
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Current Opportunities - shown for volunteer and partner tabs */}
      {(activeTab === "volunteer" || activeTab === "partner") && (
        <section className="section-spacing bg-white" data-testid="opportunities-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Current Opportunities</span>
              <h2 className="text-heading-2 text-deep-blue mt-2">Ways You Can Help</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {opportunities.map((opp) => (
                <div key={opp.title} className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid={`opportunity-${opp.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="w-12 h-12 bg-deep-blue rounded-lg flex items-center justify-center mb-4">
                    <opp.icon className="w-6 h-6 text-awareness-blue" />
                  </div>
                  <h3 className="font-bold text-deep-blue text-lg mb-3">{opp.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{opp.description}</p>
                  <div className="space-y-2 border-t border-slate-200 pt-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-deep-blue">Time:</span>
                      <span className="text-slate-600">{opp.commitment}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-deep-blue">Ideal for:</span>
                      <span className="text-slate-600">{opp.skills}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community / Updates section - shown for supporter tab */}
      {activeTab === "supporter" && (
        <section className="section-spacing bg-white" data-testid="community-updates-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Stay Connected</span>
              <h2 className="text-heading-2 text-deep-blue mt-2">What You'll Receive</h2>
              <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
                When you join our community, you'll stay informed about our progress and events.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="text-center p-6 bg-slate-50 rounded-xl border border-slate-200">
                <Megaphone className="w-10 h-10 text-awareness-blue mx-auto mb-3" />
                <h3 className="font-bold text-deep-blue mb-2">Monthly Newsletter</h3>
                <p className="text-slate-600 text-sm">Event recaps, survivor stories, and upcoming programs delivered to your inbox.</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-xl border border-slate-200">
                <Globe className="w-10 h-10 text-awareness-blue mx-auto mb-3" />
                <h3 className="font-bold text-deep-blue mb-2">Event Invitations</h3>
                <p className="text-slate-600 text-sm">Early access to event registrations, VIP opportunities, and community gatherings.</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-xl border border-slate-200">
                <Heart className="w-10 h-10 text-awareness-blue mx-auto mb-3" />
                <h3 className="font-bold text-deep-blue mb-2">Impact Updates</h3>
                <p className="text-slate-600 text-sm">See how your support is making a difference with screening stats and community reach.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Form Section */}
      <section className="section-spacing" id="involvement-form" data-testid="involvement-form">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-heading-2 text-deep-blue mb-6">
                {activeTab === "volunteer" && "Volunteer Sign-Up"}
                {activeTab === "sponsor" && "Sponsorship Inquiry"}
                {activeTab === "partner" && "Partnership Request"}
                {activeTab === "supporter" && "Join Our Community"}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Doe" required data-testid="involved-name" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" required data-testid="involved-email" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" data-testid="involved-phone" />
                  </div>
                  {(activeTab === "sponsor" || activeTab === "partner") && (
                    <div>
                      <Label htmlFor="organization">Organization</Label>
                      <Input id="organization" value={formData.organization} onChange={(e) => setFormData({...formData, organization: e.target.value})} placeholder="Company Name" data-testid="involved-org" />
                    </div>
                  )}
                </div>

                {activeTab === "volunteer" && (
                  <div>
                    <Label className="mb-3 block">Areas of Interest</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {interestOptions.map((interest) => (
                        <div key={interest} className="flex items-center space-x-2">
                          <Checkbox id={interest} checked={formData.interests.includes(interest)} onCheckedChange={() => handleInterestToggle(interest)} />
                          <Label htmlFor={interest} className="text-sm cursor-pointer">{interest}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="message">
                    {activeTab === "volunteer" && "Tell us about yourself"}
                    {activeTab === "sponsor" && "Sponsorship interests & preferred level"}
                    {activeTab === "partner" && "Partnership proposal"}
                    {activeTab === "supporter" && "How did you hear about us?"}
                  </Label>
                  <Textarea id="message" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Share any additional information..." rows={4} data-testid="involved-message" />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="updates" checked={formData.receiveUpdates} onCheckedChange={(checked) => setFormData({...formData, receiveUpdates: checked})} />
                  <Label htmlFor="updates" className="text-sm cursor-pointer">
                    I'd like to receive email updates about Blue vs Blue events, news, and impact reports
                  </Label>
                </div>

                <Button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="involved-submit">
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </form>
            </div>

            {/* Info Panel */}
            <div className="space-y-8">
              <div className="bg-slate-50 p-8 rounded-lg">
                <h3 className="text-heading-3 text-deep-blue mb-4">Why Get Involved?</h3>
                <ul className="space-y-4">
                  {[
                    "Make a direct impact on prostate cancer awareness",
                    "Connect with a community of dedicated supporters",
                    "Help first responders and their families",
                    "Gain valuable experience and skills"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-awareness-blue flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-deep-blue text-white p-8 rounded-lg">
                <h3 className="text-xl font-bold mb-2 font-heading">Have Questions?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Not sure which option is right for you? Reach out and we'll help you find the perfect way to contribute.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowRight className="w-4 h-4 text-awareness-blue" />
                    <span>info@bluevsblue.org</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowRight className="w-4 h-4 text-awareness-blue" />
                    <span>(317) 946-8365</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
